// Slice 7: client-side Markdown export. Pure function — given a run, its findings, and the set of
// selected CIDs, produces a CID-FIRST Markdown document (account → template → section → row). This
// deliberately inverts the on-screen template-first layout so a reader can follow one account's
// audit end-to-end — the single-account narrative the UI doesn't otherwise surface.
//
// Interpretive depth (v1): per finding we emit severity + the summary/LLM-verdict narrative +
// grounded-in points + the auditor's triage decision/note. Structured evidence tables are out of
// scope (would need a per-check serializer); the interpretive content is the substance.

import { format, parseISO } from 'date-fns';

import {
  PLATFORM_LABEL,
  sectionRank,
  type AuditRun,
  type Finding,
  type Severity,
  type Template,
  type Triage
} from '../types';

const TEMPLATE_LABEL: Record<Template, string> = {
  search: 'Search',
  shopping: 'Shopping',
  pmax: 'Performance Max'
};
const TEMPLATE_ORDER: Template[] = ['search', 'shopping', 'pmax'];

const SEVERITY_LABEL: Record<Severity, string> = {
  large: 'Large',
  medium: 'Medium',
  small: 'Small',
  no_issue: 'Clean',
  na: 'N/A'
};

const TRIAGE_LABEL: Record<Triage, string> = {
  untriaged: '',
  will_address: 'Will address',
  acknowledged: 'Acknowledged',
  discuss: 'Discuss',
  by_design: 'By design',
  wont_fix: "Won't fix"
};

function severityText(f: Finding): string {
  if (f.status === 'unavailable') return 'Unavailable';
  return SEVERITY_LABEL[f.severity] ?? f.severity;
}

// The interpretive body for a finding, mirroring what shows on the card above the collapse fold:
// unavailable → the reason; judgment rows → the verdict + grounded-in; deterministic → the summary.
function interpretiveBody(f: Finding): string {
  if (f.status === 'unavailable') {
    return f.statusReason ? f.statusReason.trim() : '_Not available for this account._';
  }
  if (f.llmVerdict) {
    let body = f.llmVerdict.draft.trim();
    body += `\n\n_Confidence: ${f.llmVerdict.confidence}._`;
    if (f.llmVerdict.groundedIn.length > 0) {
      body += `\n\nGrounded in:\n${f.llmVerdict.groundedIn.map(g => `- ${g}`).join('\n')}`;
    }
    return body;
  }
  if (f.summary) return f.summary.trim();
  return '_No summary for this finding._';
}

function triageLine(f: Finding): string | null {
  if (f.triage === 'untriaged') return null;
  const note = f.auditorNote.trim();
  return `_Triage: ${TRIAGE_LABEL[f.triage]}${note ? ` — "${note}"` : ''}_`;
}

export interface MarkdownExportResult {
  markdown: string;
  filename: string;
}

export function buildAuditMarkdown(
  run: AuditRun,
  findings: Finding[],
  selectedCids: Set<string>
): MarkdownExportResult {
  const dateLabel = format(parseISO(run.triggeredAt), 'MMMM d, yyyy');
  const windowLabel = `${format(parseISO(run.windowStart), 'MMM d, yyyy')} – ${format(parseISO(run.windowEnd), 'MMM d, yyyy')}`;
  const inScopeAccounts = run.cids.filter(c => c.activeTemplates.length > 0);
  const isAll = selectedCids.size >= inScopeAccounts.length;

  const lines: string[] = [];
  lines.push('# Away — Paid Media Audit');
  lines.push('');
  lines.push(`**Audit date:** ${dateLabel}  `);
  lines.push(`**Reporting window:** ${windowLabel} (trailing 90 days)  `);
  lines.push(
    `**Scope:** ${isAll ? 'All accounts (Google + Microsoft)' : `${selectedCids.size} of ${inScopeAccounts.length} accounts`}`
  );
  lines.push('');

  let emittedAccounts = 0;
  for (const account of run.cids) {
    if (!selectedCids.has(account.cid)) continue;
    const accountFindings = findings.filter(f => f.cid === account.cid);
    if (accountFindings.length === 0) continue;
    emittedAccounts += 1;

    lines.push('---');
    lines.push('');
    lines.push(`## ${account.name} — ${PLATFORM_LABEL[account.platform]} (${account.cid})`);
    lines.push('');

    for (const template of TEMPLATE_ORDER) {
      const templateFindings = accountFindings.filter(f => f.template === template);
      if (templateFindings.length === 0) continue;
      lines.push(`### ${TEMPLATE_LABEL[template]}`);
      lines.push('');

      const sections = [...new Set(templateFindings.map(f => f.section))].sort(
        (a, b) => sectionRank(a) - sectionRank(b) || a.localeCompare(b)
      );
      for (const section of sections) {
        lines.push(`#### ${section}`);
        lines.push('');
        const rows = templateFindings
          .filter(f => f.section === section)
          .sort((a, b) => a.templateRow.localeCompare(b.templateRow));
        for (const f of rows) {
          lines.push(`**${f.templateRow}** — ${severityText(f)}`);
          lines.push('');
          lines.push(interpretiveBody(f));
          const triage = triageLine(f);
          if (triage) {
            lines.push('');
            lines.push(triage);
          }
          lines.push('');
        }
      }
    }
  }

  if (emittedAccounts === 0) {
    lines.push('_No findings in the selected scope._');
    lines.push('');
  }

  const scopeSuffix = isAll ? 'all' : emittedAccounts === 1 ? 'account' : 'subset';
  const datePart = format(parseISO(run.triggeredAt), 'yyyy-MM-dd');
  const filename = `away-audit-${datePart}-${scopeSuffix}.md`;

  return { markdown: lines.join('\n'), filename };
}
